import { lazy, Suspense, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, User, Layers, CreditCard, ChevronRight, ShieldCheck, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import Reveal from "@/components/Reveal";
import HumanAi from "@/components/marketing/HumanAi";
import AgentNetwork from "@/components/marketing/AgentNetwork";
import AutomationHub from "@/components/marketing/AutomationHub";
import TrustStrip from "@/components/marketing/TrustStrip";
import { SectionLabel } from "@/components/common";
import { howItWorks } from "@/data/demoData";
import { AI_IMAGES } from "@/lib/aiImages";
import { useLanguage } from "@/context/LanguageContext";

const CoreVisual = lazy(() => import("@/components/marketing/IntelligenceCore"));

function HeroCore() {
  const [use3d, setUse3d] = useState(false);
  useEffect(() => {
    const ok =
      window.matchMedia("(min-width: 768px)").matches &&
      !window.matchMedia("(prefers-reduced-motion: reduce)").matches &&
      !!window.WebGLRenderingContext;
    setUse3d(ok);
  }, []);
  const img = (
    <img
      src={AI_IMAGES.core}
      alt="Vvvoraa AI intelligence core surrounded by connected AI agents"
      className="w-full rounded-3xl border border-white/10 shadow-[0_40px_90px_-30px_rgba(0,0,0,0.9)]"
      data-testid="intelligence-core-fallback"
    />
  );
  if (!use3d) return img;
  return <Suspense fallback={img}><CoreVisual /></Suspense>;
}

export default function Home() {
  const navigate = useNavigate();
  const { t } = useLanguage();

  return (
    <div>
      {/* ================= HERO ================= */}
      <section className="relative overflow-hidden vora-hero-radial">
        <div className="absolute inset-0 vora-grid-bg opacity-40" aria-hidden />
        <div className="relative mx-auto max-w-7xl px-5 sm:px-8 pt-16 pb-24 lg:pt-24 lg:pb-32">
          <div className="grid lg:grid-cols-2 gap-14 lg:gap-10 items-center">
            <div>
              <div className="vora-fade-up inline-flex items-center gap-2 rounded-full border border-[#4F8CFF]/25 bg-[#4F8CFF]/8 px-3.5 py-1.5" data-testid="hero-badge">
                <span className="h-1.5 w-1.5 rounded-full bg-[#4F8CFF]" />
                <span className="text-[11px] font-mono uppercase tracking-[0.2em] text-[#8fb4ff]">
                  {t("home.badge")}
                </span>
              </div>

              <h1 className="vora-fade-up mt-6 text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.05]" style={{ animationDelay: "0.05s" }}>
                {t("home.h1a")} <br className="hidden sm:block" />
                {t("home.h1b")} <span className="vora-text-gradient">{t("home.h1accent")}</span>
              </h1>

              <p className="vora-fade-up mt-6 max-w-xl text-lg text-slate-300 leading-relaxed" style={{ animationDelay: "0.12s" }}>
                {t("home.sub")}
              </p>

              <div className="vora-fade-up mt-8 flex flex-col sm:flex-row gap-3" style={{ animationDelay: "0.18s" }}>
                <Button
                  onClick={() => navigate("/contact")}
                  className="h-12 px-6 bg-[#4F8CFF] hover:bg-[#3f7af0] text-white rounded-full text-base font-semibold group"
                  data-testid="hero-cta-talk"
                >
                  {t("home.cta1")}
                  <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
                </Button>
                <Button
                  onClick={() => navigate("/solutions")}
                  variant="outline"
                  className="h-12 px-6 bg-transparent border-white/15 text-white hover:bg-white/5 rounded-full text-base"
                  data-testid="hero-cta-solutions"
                >
                  {t("home.cta2")}
                </Button>
              </div>

              <p className="vora-fade-up mt-6 text-sm text-slate-400" style={{ animationDelay: "0.24s" }}>
                {t("home.trusted")}
              </p>
            </div>

            <div className="vora-fade-up" style={{ animationDelay: "0.2s" }}>
              <HeroCore />
            </div>
          </div>
        </div>
      </section>

      {/* ================= PROBLEM ================= */}
      <section className="relative py-24 border-t border-white/8">
        <div className="mx-auto max-w-5xl px-5 sm:px-8">
          <Reveal>
            <SectionLabel>The Problem</SectionLabel>
            <h2 className="mt-4 text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight max-w-3xl">
              AI agents are becoming autonomous. Payments shouldn't be.
            </h2>
          </Reveal>

          <div className="mt-10 grid md:grid-cols-2 gap-10">
            <Reveal delay={0.05}>
              <p className="text-base text-slate-300 leading-relaxed">
                Traditional payment systems were designed around humans manually initiating transactions.
              </p>
              <p className="mt-4 text-base text-slate-300 leading-relaxed">
                AI agents can research, purchase services, subscribe to software, execute workflows and
                interact with businesses continuously.
              </p>
            </Reveal>
            <Reveal delay={0.1}>
              <div className="vora-card rounded-2xl p-6">
                <p className="text-base text-slate-200 leading-relaxed">
                  Vvvoraa creates a control layer between the agent and money.
                </p>
                <div className="mt-5 space-y-3">
                  {[
                    "The agent can act.",
                    "The founder defines the boundaries.",
                    "Vvvoraa enforces them.",
                  ].map((t, i) => (
                    <div key={i} className="flex items-center gap-3">
                      <span className="h-6 w-6 rounded-full bg-[#4F8CFF]/15 text-[#4F8CFF] text-xs flex items-center justify-center font-semibold">
                        {i + 1}
                      </span>
                      <span className="text-sm text-white">{t}</span>
                    </div>
                  ))}
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ================= CONTROL RELATIONSHIP ================= */}
      <section className="py-20 border-t border-white/8 bg-[#07111F]/40">
        <div className="mx-auto max-w-4xl px-5 sm:px-8">
          <Reveal className="text-center">
            <SectionLabel>The Core Principle</SectionLabel>
            <h2 className="mt-4 text-2xl sm:text-3xl font-semibold tracking-tight">
              Vvvoraa does not remove human control. It makes controlled AI autonomy possible.
            </h2>
          </Reveal>
          <Reveal delay={0.08}>
            <div className="mt-12 flex flex-col items-center gap-3">
              {[
                { icon: User, label: "Human / Founder", sub: "Defines intent" },
                { icon: ShieldCheck, label: "Vvvoraa Policies & Controls", sub: "Enforces boundaries" },
                { icon: Layers, label: "AI Agent", sub: "Executes within limits" },
                { icon: CreditCard, label: "Payment Action", sub: "Only when approved" },
              ].map((n, i, arr) => {
                const Icon = n.icon;
                return (
                  <div key={n.label} className="w-full flex flex-col items-center">
                    <div className="w-full max-w-md vora-card rounded-2xl px-5 py-4 flex items-center gap-4 vora-card-hover">
                      <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-gradient-to-br from-[#4F8CFF]/20 to-[#7C5CFF]/20 text-[#4F8CFF]">
                        <Icon className="h-5 w-5" />
                      </span>
                      <div>
                        <p className="text-sm font-medium text-white">{n.label}</p>
                        <p className="text-xs text-slate-400">{n.sub}</p>
                      </div>
                    </div>
                    {i < arr.length - 1 && (
                      <div className="h-7 w-px bg-gradient-to-b from-[#4F8CFF]/50 to-transparent my-1" />
                    )}
                  </div>
                );
              })}
            </div>
          </Reveal>
        </div>
      </section>

      {/* ================= HUMAN + AI ================= */}
      <HumanAi />

      {/* ================= SOLUTIONS ================= */}
      <section id="features" className="py-24 border-t border-white/8 scroll-mt-20">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <Reveal>
            <SectionLabel>{t("home.solLabel")}</SectionLabel>
            <h2 className="mt-4 text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight max-w-3xl">
              {t("home.solH2")}
            </h2>
            <Link to="/solutions" className="mt-4 inline-flex items-center gap-1.5 text-sm text-[#4F8CFF] hover:text-[#8fb4ff] transition-colors" data-testid="solutions-see-all">
              {t("home.seeAll")} <ChevronRight className="h-4 w-4" />
            </Link>
          </Reveal>

          <div className="mt-12 grid lg:grid-cols-5 gap-10 items-start">
            <Reveal delay={0.05} className="lg:col-span-3">
              <div className="space-y-4 text-base text-slate-300 leading-relaxed" data-testid="solutions-intro">
                <p>{t("home.solBody")}</p>
              </div>
              <div className="mt-8 flex flex-col sm:flex-row gap-3">
                <Button
                  onClick={() => navigate("/contact")}
                  className="h-12 px-6 bg-[#4F8CFF] hover:bg-[#3f7af0] text-white rounded-full text-base font-semibold group"
                  data-testid="solutions-cta-contact"
                >
                  {t("home.cta1")}
                  <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
                </Button>
                <Button
                  onClick={() => navigate("/ai")}
                  variant="outline"
                  className="h-12 px-6 bg-transparent border-white/15 text-white hover:bg-white/5 rounded-full text-base"
                  data-testid="solutions-cta-ai-enquiry"
                >
                  <Sparkles className="mr-2 h-4 w-4 text-[#4F8CFF]" />
                  {t("home.ctaAi")}
                </Button>
              </div>
            </Reveal>

            <Reveal delay={0.1} className="lg:col-span-2">
              <div className="vora-card rounded-2xl p-6 sm:p-7" data-testid="solutions-capabilities-card">
                <p className="text-[11px] font-mono uppercase tracking-[0.18em] text-slate-500">{t("home.capLabel")}</p>
                <div className="mt-4 flex flex-wrap gap-2">
                  {t("home.caps").map((c) => (
                    <span key={c} className="text-xs rounded-full border border-[#4F8CFF]/25 bg-[#4F8CFF]/8 px-3 py-1.5 text-slate-200">
                      {c}
                    </span>
                  ))}
                </div>
                <p className="mt-5 text-sm text-slate-400 leading-relaxed">
                  {t("home.capNote")}
                </p>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ================= AGENT NETWORK ================= */}
      <AgentNetwork />

      {/* ================= AUTOMATION HUB ================= */}
      <AutomationHub />

      {/* ================= ECOSYSTEM BAND ================= */}
      <section className="relative border-t border-white/8" data-testid="ecosystem-band">
        <div className="relative h-[340px] sm:h-[440px] overflow-hidden">
          <img src={AI_IMAGES.ecosystem} alt="Connected digital business ecosystem powered by AI agents" className="absolute inset-0 h-full w-full object-cover opacity-70" loading="lazy" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#05080D] via-[#05080D]/30 to-[#05080D]" aria-hidden />
          <div className="relative mx-auto max-w-7xl px-5 sm:px-8 h-full flex items-end pb-12">
            <Reveal>
              <SectionLabel>{t("home.ecoLabel")}</SectionLabel>
              <h2 className="mt-3 text-2xl sm:text-3xl font-semibold tracking-tight max-w-xl">{t("home.ecoH2")}</h2>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ================= HOW IT WORKS ================= */}
      <section id="how-it-works" className="py-24 border-t border-white/8 bg-[#07111F]/40 scroll-mt-20">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <Reveal>
            <SectionLabel>How It Works</SectionLabel>
            <h2 className="mt-4 text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight">
              Three steps to controlled autonomy.
            </h2>
          </Reveal>

          <div className="mt-14 grid md:grid-cols-3 gap-5">
            {howItWorks.map((s, i) => (
              <Reveal key={s.step} delay={i * 0.08}>
                <div className="h-full vora-card rounded-2xl p-7">
                  <p className="font-mono text-4xl font-semibold text-[#4F8CFF]/40">{s.step}</p>
                  <h3 className="mt-4 text-lg font-semibold text-white tracking-wide">{s.title}</h3>
                  <p className="mt-2 text-sm text-slate-400 leading-relaxed">{s.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>

          <Reveal delay={0.1}>
            <div className="mt-10 vora-card rounded-2xl p-8">
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-6">
                {["AI Agent", "Vvvoraa Control Layer", "Payment"].map((n, i, arr) => (
                  <div key={n} className="flex items-center gap-4 sm:gap-6">
                    <div className={`rounded-xl px-5 py-3 text-sm font-medium ${
                      i === 1
                        ? "bg-gradient-to-r from-[#4F8CFF] to-[#7C5CFF] text-white vora-glow-blue"
                        : "border border-white/12 text-white bg-white/[0.03]"
                    }`}>
                      {n}
                    </div>
                    {i < arr.length - 1 && <ChevronRight className="h-5 w-5 text-slate-500 rotate-90 sm:rotate-0" />}
                  </div>
                ))}
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ================= TRUST ================= */}
      <TrustStrip />

      {/* ================= FOUNDER ================= */}
      <section className="py-24 border-t border-white/8">
        <div className="mx-auto max-w-5xl px-5 sm:px-8">
          <Reveal>
            <SectionLabel>Our Principle</SectionLabel>
            <h2 className="mt-4 text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight max-w-3xl">
              Built for the companies building autonomous operations.
            </h2>
          </Reveal>
          <div className="mt-10 grid lg:grid-cols-5 gap-8 items-start">
            <Reveal delay={0.05} className="lg:col-span-3">
              <div className="space-y-4 text-base text-slate-300 leading-relaxed">
                <p>AI agents are moving from answering questions to taking action.</p>
                <p>
                  That means they will increasingly need to interact with the real economy — purchasing
                  software, accessing services, running workflows and executing business operations.
                </p>
                <p>Vvvoraa is being built around one simple principle:</p>
                <p className="text-xl font-semibold text-white">
                  Let your agents act. Keep humans in control of money.
                </p>
              </div>
            </Reveal>
            <Reveal delay={0.1} className="lg:col-span-2">
              <div className="vora-card rounded-2xl p-6">
                <p className="text-[11px] font-mono uppercase tracking-[0.18em] text-slate-500">Vvvoraa</p>
                <p className="mt-3 text-lg font-semibold text-white">AI agents can act. Vvvoraa controls how they spend.</p>
                <div className="mt-6 vora-divider-glow" />
                <p className="mt-6 text-sm text-slate-400">
                  The sovereign financial control layer for autonomous AI — programmable budgets, approvals and monitoring.
                </p>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ================= SECURITY PREVIEW ================= */}
      <section className="py-20 border-t border-white/8 bg-[#07111F]/40">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <div className="vora-card rounded-3xl p-8 sm:p-12 flex flex-col lg:flex-row lg:items-center justify-between gap-8">
            <Reveal>
              <SectionLabel>Security</SectionLabel>
              <h2 className="mt-4 text-2xl sm:text-3xl font-semibold tracking-tight">Autonomy with boundaries.</h2>
              <p className="mt-3 max-w-xl text-base text-slate-400">
                Give agents the ability to act without giving them unlimited financial access.
              </p>
            </Reveal>
            <Reveal delay={0.08}>
              <Button asChild className="h-12 px-6 bg-white text-[#07111F] font-semibold rounded-full hover:bg-slate-100">
                <Link to="/security" data-testid="security-preview-cta">
                  Explore Security <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ================= FINAL CTA ================= */}
      <section className="py-28 border-t border-white/8 relative overflow-hidden">
        <div className="absolute inset-0 vora-hero-radial opacity-70" aria-hidden />
        <div className="relative mx-auto max-w-3xl px-5 sm:px-8 text-center">
          <Reveal>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight">
              Your agents are ready to act.
            </h2>
            <p className="mt-5 text-lg text-slate-300">
              Give them a wallet — not unlimited access to your money.
            </p>
            <div className="mt-9 flex flex-col sm:flex-row gap-3 justify-center">
              <Button onClick={() => navigate("/early-access")} className="h-12 px-7 bg-[#4F8CFF] hover:bg-[#3f7af0] text-white rounded-full text-base font-semibold" data-testid="final-cta-early-access">
                Get Early Access
              </Button>
              <Button onClick={() => navigate("/developers")} variant="outline" className="h-12 px-7 bg-transparent border-white/15 text-white hover:bg-white/5 rounded-full text-base" data-testid="final-cta-build">
                Build with Vvvoraa
              </Button>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
